import { useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, LinearProgress, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Explain } from '../../Work/explain';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, PieChart, Pie, Cell,
} from 'recharts';
import {
    REQUEST_STATUSES, REQUEST_PRIORITIES,
    STATUS_CHIP_COLORS, PRIORITY_CHIP_COLORS,
} from './constants';

const OUTSTANDING = new Set(['Open', 'In Progress', 'Blocked']);
// Categorical palette for per-client bars (cycled).
const CLIENT_PALETTE = ['#378ADD', '#1D9E75', '#EF9F27', '#D4537E', '#7F77DD', '#0AADCC', '#E24B4A', '#639922'];

const StatTile = ({ label, value, accent, explain }) => (
    <Explain text={explain} block>
    <Paper elevation={0} sx={{ p: 2, border: '1px solid var(--color-divider)', borderRadius: 2, backgroundColor: 'var(--color-bg-paper)' }}>
        <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
        <Typography className="nexus-font" sx={{ fontSize: 30, fontWeight: 700, color: accent || 'var(--color-text-primary)', lineHeight: 1.2 }}>{value}</Typography>
    </Paper>
    </Explain>
);

// Compact toggle for including completed items in a chart. Filled (green) =
// completed included; outlined = completed hidden (the default).
const CompletedToggle = ({ on, onToggle }) => (
    <Tooltip title={on ? 'Completed included — click to hide' : 'Completed hidden — click to include'}>
        <Chip
            label="Completed"
            size="small"
            onClick={onToggle}
            variant={on ? 'filled' : 'outlined'}
            sx={{
                height: 22, fontSize: 11, cursor: 'pointer',
                ...(on ? { backgroundColor: '#2e7d32', color: '#fff' } : { color: 'var(--color-text-secondary)' }),
            }}
        />
    </Tooltip>
);

const ChartCard = ({ title, action, children, height = 260, explain }) => (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid var(--color-divider)', borderRadius: 2, backgroundColor: 'var(--color-bg-paper)', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
            <Explain text={explain}><Typography className="nexus-font" sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography></Explain>
            {action}
        </Box>
        <Box sx={{ height }}>{children}</Box>
    </Paper>
);

const withoutCompleted = (list, include) => (include ? list : list.filter((r) => r.Status !== 'Completed'));

const RequestsDashboard = ({ requests = [], loading = false }) => {
    const theme = useTheme();
    const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
    const statusColors = STATUS_CHIP_COLORS[mode];
    const priorityColors = PRIORITY_CHIP_COLORS[mode];

    // Charts hide completed items by default; each has its own include toggle.
    const [statusIncl, setStatusIncl] = useState(false);
    const [priorityIncl, setPriorityIncl] = useState(false);
    const [clientIncl, setClientIncl] = useState(false);

    const stats = useMemo(() => {
        const total = requests.length;
        const outstanding = requests.filter((r) => OUTSTANDING.has(r.Status)).length;
        const completed = requests.filter((r) => r.Status === 'Completed').length;
        const highPriority = requests.filter((r) => r.Priority === 'High' || r.Priority === 'Urgent').length;
        return { total, outstanding, completed, highPriority };
    }, [requests]);

    const statusData = useMemo(() => {
        const src = withoutCompleted(requests, statusIncl);
        return REQUEST_STATUSES.map((s) => ({ name: s, value: src.filter((r) => r.Status === s).length }))
            .filter((d) => d.value > 0);
    }, [requests, statusIncl]);

    const priorityData = useMemo(() => {
        const src = withoutCompleted(requests, priorityIncl);
        return REQUEST_PRIORITIES.map((p) => ({ name: p, value: src.filter((r) => r.Priority === p).length }))
            .filter((d) => d.value > 0);
    }, [requests, priorityIncl]);

    // Per-client breakdown table: total / outstanding / completed (always full).
    const byClient = useMemo(() => {
        const map = new Map();
        for (const r of requests) {
            const name = r.ClientName || 'Unassigned';
            if (!map.has(name)) map.set(name, { name, total: 0, outstanding: 0, completed: 0 });
            const bucket = map.get(name);
            bucket.total += 1;
            if (OUTSTANDING.has(r.Status)) bucket.outstanding += 1;
            if (r.Status === 'Completed') bucket.completed += 1;
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [requests]);

    // "Requests by client" bar chart honours the completed toggle.
    const topClients = useMemo(() => {
        const src = withoutCompleted(requests, clientIncl);
        const map = new Map();
        for (const r of src) {
            const name = r.ClientName || 'Unassigned';
            map.set(name, (map.get(name) || 0) + 1);
        }
        return Array.from(map.entries())
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 12);
    }, [requests, clientIncl]);

    if (loading && requests.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    if (requests.length === 0) {
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px dashed var(--color-divider)', borderRadius: 2 }}>
                <Typography sx={{ color: 'var(--color-text-secondary)' }}>
                    No requests yet. Switch to “All Requests” to create the first one.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {/* KPI tiles */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}><StatTile label="Total" value={stats.total} explain="Every success request that is not soft-deleted (success.NexusSuccessRequest, IsDeleted = 0), whatever its status." /></Grid>
                <Grid item xs={6} md={3}><StatTile label="Outstanding" value={stats.outstanding} accent="#e65100" explain="Requests whose Status is Open, In Progress or Blocked. The same rule the work views use for an open request." /></Grid>
                <Grid item xs={6} md={3}><StatTile label="Completed" value={stats.completed} accent="#2e7d32" explain="Requests whose Status is exactly Completed. Cancelled requests are neither outstanding nor completed: withdrawn." /></Grid>
                <Grid item xs={6} md={3}><StatTile label="High / Urgent" value={stats.highPriority} accent="#c62828" explain="Requests whose Priority is High or Urgent, at any status. The work views map Urgent onto Critical for the cadence." /></Grid>
            </Grid>

            <Grid container spacing={2}>
                {/* Requests by client */}
                <Grid item xs={12} md={7}>
                    <ChartCard title="Requests by client" explain="Top twelve clients by request count (ClientName on the request). Completed requests are hidden unless the Completed chip is filled." action={<CompletedToggle on={clientIncl} onToggle={() => setClientIncl((v) => !v)} />}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topClients} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(150,150,150,0.2)" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: 'Verdana' }} stroke="var(--color-text-secondary)"
                                    interval={0} angle={-25} textAnchor="end" height={60} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fontFamily: 'Verdana' }} stroke="var(--color-text-secondary)" />
                                <RTooltip cursor={{ fill: 'rgba(0,77,242,0.05)' }} />
                                <Bar dataKey="total" name="Requests" radius={[4, 4, 0, 0]}>
                                    {topClients.map((c, i) => <Cell key={c.name} fill={CLIENT_PALETTE[i % CLIENT_PALETTE.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Status donut */}
                <Grid item xs={12} sm={6} md={2.5}>
                    <ChartCard title="By status" explain="Requests per Status value: Open, In Progress, Blocked, Completed, Cancelled. Completed hidden unless the chip is filled." action={<CompletedToggle on={statusIncl} onToggle={() => setStatusIncl((v) => !v)} />}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={38} outerRadius={70} paddingAngle={2}>
                                    {statusData.map((d) => <Cell key={d.name} fill={statusColors[d.name]?.text || '#888'} />)}
                                </Pie>
                                <RTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Priority donut */}
                <Grid item xs={12} sm={6} md={2.5}>
                    <ChartCard title="By priority" explain="Requests per Priority value: Low, Medium, High, Urgent. Completed hidden unless the chip is filled." action={<CompletedToggle on={priorityIncl} onToggle={() => setPriorityIncl((v) => !v)} />}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius={38} outerRadius={70} paddingAngle={2}>
                                    {priorityData.map((d) => <Cell key={d.name} fill={priorityColors[d.name]?.text || '#888'} />)}
                                </Pie>
                                <RTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>

                {/* Per-client table with completion progress */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid var(--color-divider)', borderRadius: 2, backgroundColor: 'var(--color-bg-paper)' }}>
                        <Explain text="Per client: total requests, outstanding (Open / In Progress / Blocked), completed, and completed as a share of total. Always over every request, ignoring the chart toggles."><Typography className="nexus-font" sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Client breakdown</Typography></Explain>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 0.8fr 0.8fr 1.4fr', gap: 1, alignItems: 'center' }}>
                            {['Client', 'Total', 'Outstanding', 'Completed', 'Completion'].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-secondary)' }}>{h}</Typography>
                            ))}
                            {byClient.map((c) => {
                                const pct = c.total ? Math.round((c.completed / c.total) * 100) : 0;
                                return [
                                    <Typography key={`${c.name}-n`} sx={{ fontSize: 13 }}>{c.name}</Typography>,
                                    <Typography key={`${c.name}-t`} sx={{ fontSize: 13 }}>{c.total}</Typography>,
                                    <Typography key={`${c.name}-o`} sx={{ fontSize: 13, color: '#e65100' }}>{c.outstanding}</Typography>,
                                    <Typography key={`${c.name}-c`} sx={{ fontSize: 13, color: '#2e7d32' }}>{c.completed}</Typography>,
                                    <Box key={`${c.name}-p`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress variant="determinate" value={pct}
                                            sx={{ flexGrow: 1, height: 6, borderRadius: 3, backgroundColor: 'var(--color-bg-hover)', '& .MuiLinearProgress-bar': { backgroundColor: '#2e7d32' } }} />
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)', minWidth: 32, textAlign: 'right' }}>{pct}%</Typography>
                                    </Box>,
                                ];
                            })}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default RequestsDashboard;
